import {
  deletePostComment,
  postCommentsDislikes,
  postCommentsLikes,
} from "@/api/comment";
import HeartAnimation from "@/components/custom-framer-animations/heart-animation";
import AuthorTag from "@/components/content-modal-comps/author-tag";
import Iconify from "@/components/ui/Iconify";
import PostAvatar from "@/components/ui/PostAvatar";
import Span from "@/components/ui/Span";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { getIdentifier, timeAgo, timeAgoInKorean } from "@/utils/helpers";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, Menu, MenuItem } from "@mui/material";
import Image from "next/image";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import ContentSubCommentList from "../content-sub-comment-list";
import PostCmtStickyTag from "../content-comment-sticky-tag";
import { updatePost } from "@/api/posts";
import ContentCommentContent from "../content-comment-content";
import { useTheme } from "next-themes";
import TooltipUserNameheading from "../content-sections/content-details-section/content-tooltip-username-heading";
import { useLocale, useTranslations } from "@/lib/static-intl";
import { CLOUDFRONT_BASE_URL } from "../../../../config";

const ContentCommentListTile = ({
  comment,
  isSticky,
  setPinnedCommentId,
  setAllPostComments,
  selectedPostInModal,
  scrollToCommentSection,
  setCurrentCommentInReply,
  setTotalComments,
  triggerReportModal,
  openProfile,
  triggerFullScreenImageSlider,
  currentUserFollowing,
  setCurrentUserFollowing,
}) => {
  const locale = useLocale();
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");

  const { theme } = useTheme();
  const [cmtMenuAnchorEl, setCmtMenuAnchorEl] = useState(null);
  const commentMenuOpen = Boolean(cmtMenuAnchorEl);

  const { user, openLoginModal } = useAuthContext();
  const [commentLike, setCommentLike] = useState(comment?.commentLike);
  const [isCommentLiked, setIsCommentLiked] = useState(
    !!commentLike?.find((_cl) => _cl?.userId === user?.userId)
  );

  const handleCmtMenuClick = (event) => {
    if (!user) {
      openLoginModal();
      return;
    }
    setCmtMenuAnchorEl(event.currentTarget);
  };
  const handleCmtMenuClose = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    setCmtMenuAnchorEl(null);
  };

  const likeComment = async (userId, commentId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      if (!user || !comment) return;
      await postCommentsLikes({
        userId,
        commentId,
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const dislikeComment = async (userId, commentId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      if (!user || !comment) return;
      await postCommentsDislikes({
        userId,
        commentId,
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handleLikeComment = () => {
    if (!user) {
      openLoginModal();
      return;
    }

    if (!commentLike?.some((like) => like.userId === user?.userId)) {
      setCommentLike([
        ...commentLike,
        { commentId: comment?.id, userId: user?.userId },
      ]);
      setIsCommentLiked(true);
      likeComment(user?.userId, comment?.id);
    } else {
      setCommentLike(
        commentLike?.filter((like) => like.userId !== user?.userId)
      );
      setIsCommentLiked(false);
      dislikeComment(user?.userId, comment?.id);
    }
  };

  const handlePinComment = async (cmtId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!cmtId || !selectedPostInModal?.id) return;
    setPinnedCommentId(cmtId);
    scrollToCommentSection();
    handleCmtMenuClose();
    try {
      const payload = {
        pinnedCommentId: cmtId,
      };

      await updatePost(payload, selectedPostInModal?.id);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  const handleUnpinComment = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!selectedPostInModal?.id) return;
    setPinnedCommentId(null);
    handleCmtMenuClose();

    try {
      const payload = {
        pinnedCommentId: null,
      };

      await updatePost(payload, selectedPostInModal?.id);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  const handleDeleteComment = async (cmtId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!cmtId) return;
    setTotalComments((prev) => prev - 1);
    setAllPostComments((prev) => prev?.filter((cmt) => cmt?.id !== cmtId));

    try {
      await deletePostComment(cmtId);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    }
  };

  const handleReportComment = (cmt) => {
    if (!user) {
      openLoginModal();
      return;
    }
    handleCmtMenuClose();
    triggerReportModal(cmt);
  };

  const getCommentLikeText = (commentLike) => {
    if (commentLike?.length === 0) {
      return explorePostDetailsT("thumbs_up");
    } else if (commentLike?.length > 10) {
      return "10+";
    } else {
      return commentLike?.length;
    }
  };

  useEffect(() => {
    setCommentLike(comment?.commentLike);
    setIsCommentLiked(
      !!comment?.commentLike?.find((_cl) => _cl?.userId === user?.userId)
    );
  }, [comment]);

  return (
    <div className="w-full flex gap-5">
      <div className="h-fit w-fit cursor-pointer">
        <TooltipUserNameheading
          username={comment?.user?.username}
          currentUserFollowing={currentUserFollowing}
          setCurrentUserFollowing={setCurrentUserFollowing}
        >
          <PostAvatar
            src={comment?.user?.profilePicture}
            className={"!h-10 !min-w-10"}
            onClick={() =>
              openProfile(getIdentifier(comment?.user?.username, comment?.user?.id))
            }
          />
        </TooltipUserNameheading>
      </div>

      <div className="w-full flex flex-col gap-2">
        <div className="w-full flex items-center justify-between">
          <div className="w-fit flex items-center gap-2 cursor-pointer">
            <TooltipUserNameheading
              username={comment?.user?.username}
              currentUserFollowing={currentUserFollowing}
              setCurrentUserFollowing={setCurrentUserFollowing}
            >
              <Span
                className={"!text-sm hover:!text-black dark:hover:!text-white"}
                onClick={() =>
                  openProfile(getIdentifier(comment?.user?.username, comment?.user?.id))
                }
              >
                {comment?.user?.name}
              </Span>
              {comment?.user?.id === user?.userId && <AuthorTag />}
            </TooltipUserNameheading>
          </div>
          {
            user && (comment?.user?.id === user?.userId || selectedPostInModal?.user?.id === user?.userId) && (
              <>
                <IconButton
                  id="comment-menu"
                  size="small"
                  onClick={handleCmtMenuClick}
                >
                  <MoreHorizIcon
                    sx={{ fontSize: 14, color: theme === "dark" ? "white" : "black" }}
                  />
                </IconButton>
                <Menu
                  id="comment-menu"
                  anchorEl={cmtMenuAnchorEl}
                  open={commentMenuOpen}
                  onClose={handleCmtMenuClose}
                  MenuListProps={{
                    "aria-labelledby": "comment-menu",
                    className: 'bg-white dark:bg-[#282828] text-black dark:text-white',
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  style={{
                    zIndex: 9999,
                  }}
                  sx={{
                    p: 0,
                    '& .MuiPaper-root': {
                      zIndex: 9999,
                    }
                  }}
                >
                  {[
                    selectedPostInModal?.user?.id === user?.userId &&
                    (isSticky ? (
                      <MenuItem
                        className="hover:bg-bg-black/5 dark:hover:bg-dark_bg_grey"
                        sx={{
                          backgroundColor: theme === "dark" ? "#282828" : "#fff",
                          color: theme === "dark" ? "white" : "black",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark" ? "#282828" : "inherit",
                          },
                        }}
                        key="unpin"
                        onClick={handleUnpinComment}
                      >
                        {explorePostDetailsT("unpin")}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        key="pin"
                        className="hover:bg-bg-black/5 dark:hover:bg-dark_bg_grey"
                        sx={{
                          backgroundColor: theme === "dark" ? "#282828" : "#fff",
                          color: theme === "dark" ? "white" : "black",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark" ? "#282828" : "inherit",
                          },
                        }}
                        onClick={() => handlePinComment(comment?.id)}
                      >
                        {explorePostDetailsT("pin_to_top")}
                      </MenuItem>
                    )),
                    comment?.user?.id === user?.userId && (
                      <MenuItem
                        key="delete"
                        className="hover:bg-bg-black/5 dark:hover:bg-dark_bg_grey"
                        sx={{
                          backgroundColor: theme === "dark" ? "#282828" : "#fff",
                          color: theme === "dark" ? "white" : "black",
                          "&:hover": {
                            backgroundColor: theme === "dark" ? "#282828" : "inherit",
                          },
                        }}
                        onClick={() => handleDeleteComment(comment?.id)}
                      >
                        {explorePostDetailsT("delete")}
                      </MenuItem>
                    ),
                    comment?.user?.id !== user?.userId && (
                      <MenuItem
                        key="report"
                        className="hover:bg-bg-black/5 dark:hover:bg-dark_bg_grey"
                        sx={{
                          backgroundColor: theme === "dark" ? "#282828" : "#fff",
                          color: theme === "dark" ? "white" : "black",
                          "&:hover": {
                            backgroundColor: theme === "dark" ? "#282828" : "inherit",
                          },
                        }}
                        onClick={() => handleReportComment(comment)}
                      >
                        {explorePostDetailsT("report")}
                      </MenuItem>
                    ),
                  ]}
                </Menu>
              </>
            )
          }
        </div>
        {comment?.commentMedia?.length > 0 && (
          <div
            className="min-w-40 min-h-40 w-full h-full max-w-40 max-h-40 rounded overflow-hidden relative border cursor-zoom-in"
            onClick={() =>
              triggerFullScreenImageSlider(comment?.commentMedia || [])
            }
          >
            <Image
              src={
                comment?.commentMedia[0]?.filePath?.startsWith("uploads/") ||
                comment?.commentMedia[0]?.filePath?.startsWith("/")
                  ? `${CLOUDFRONT_BASE_URL}/${comment?.commentMedia[0]?.filePath}`
                  : comment?.commentMedia[0]?.filePath
              }
              key={comment?.commentMedia[0]?.id || "comment media"}
              alt="comment"
              className="object-cover"
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          </div>
        )}
        <ContentCommentContent
          content={comment?.content}
          taggedUsers={comment?.commentTaggedUsers}
          className="!text-sm !text-black dark:!text-white"
        />
        {isSticky && <PostCmtStickyTag />}
        <Span className={"!text-sm"}>
          {locale === "en"
            ? timeAgo(comment?.createdAt)
            : timeAgoInKorean(comment?.createdAt)}
        </Span>
        <div className="w-full flex items-center gap-4 select-none">
          <button
            type="button"
            className="group flex items-center gap-1 cursor-pointer"
            onClick={handleLikeComment}
          >
            <HeartAnimation
              hasClicked={isCommentLiked}
              onClick={() => { }}
              size={16}
            />
            <Span>{getCommentLikeText(commentLike)}</Span>
          </button>

          <button
            type="button"
            className="group flex items-center gap-1 cursor-pointer"
            onClick={() => setCurrentCommentInReply(comment)}
          >
            <Iconify icon={"mage:message-round"} className={"!w-4 !h-4"} />
            <Span>{explorePostDetailsT("reply")}</Span>
          </button>
        </div>
        <ContentSubCommentList
          subComments={comment?.subComments}
          parentCommentId={comment?.id}
          setAllPostComments={setAllPostComments}
          selectedPostInModal={selectedPostInModal}
          setCurrentCommentInReply={setCurrentCommentInReply}
          handleReportComment={handleReportComment}
          triggerFullScreenImageSlider={triggerFullScreenImageSlider}
          currentUserFollowing={currentUserFollowing}
          setCurrentUserFollowing={setCurrentUserFollowing}
        />
      </div>
    </div>
  );
};

export default ContentCommentListTile;
